import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '@/context/AuthContext';
import {api} from '@/lib/api';
import {brandHomeUrl, cacheBrandHome} from '@/brandHome';

function installedStudioEntry() {
  const url = new URL(window.location.href);
  const explicitLaunch = url.searchParams.get('launch') === '1';
  const standalone = Boolean(window.navigator?.standalone)
    || Boolean(window.matchMedia?.('(display-mode: standalone)').matches);
  if (!standalone && !explicitLaunch) return null;

  // Preserve journeys opened from notifications, campaigns and deep links.
  if (url.hash) return null;
  for (const [key, value] of url.searchParams) {
    if (!['studio', 'brand', 'launch', 'view'].includes(key)) return null;
    if (key === 'view' && value !== 'home') return null;
  }
  if (window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward') return null;
  // Plain anchors within the installed app load a new document too. They are
  // explicit visits, not a relaunch from the Home Screen icon.
  try {
    if (!explicitLaunch && document.referrer && new URL(document.referrer).origin === url.origin) return null;
  } catch { /* A missing/unparseable referrer is not an internal navigation. */ }

  const studioPath = url.pathname.match(/^\/studio\/([a-z0-9-]{1,160})(?:\/login)?\/?$/i);
  if (!studioPath && !/^\/client\/?$/.test(url.pathname)) return null;
  const slug = studioPath?.[1] || url.searchParams.get('studio') || '';
  if (slug && !/^[a-z0-9-]{1,160}$/i.test(slug)) return null;
  return {slug, brandHint: url.searchParams.get('brand') || ''};
}

// Resolve once per document, before mounting any Studio workspace. An already
// installed icon can keep its original URL; the account owns the current home.
export default function BrandLaunchRestore({children}) {
  useUiLanguage();
  const {status, user} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [entry] = useState(installedStudioEntry);
  const [state, setState] = useState('loading');
  const [target, setTarget] = useState('');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const destination=new URLSearchParams(location.search);
    if(destination.get('view')==='inquiries'&&destination.get('inquiry')){if(state!=='done')setState('done');return;}
    if (!entry || state !== 'loading' || status === 'loading') return;
    if (status !== 'authed' || user?.role !== 'client') {
      setState('done');
      return;
    }
    let active = true;
    const run = async () => {
      let studio;
      if (entry.slug) {
        studio = (await api.get(`/public/studios/${encodeURIComponent(entry.slug)}`)).studio;
      } else {
        const context = await api.get('/client/me');
        const studios = (context.relationships || []).map(item => item.studio).filter(Boolean);
        const brands = [...new Set(studios.map(item => item.brand_network?.slug).filter(Boolean))];
        // Multi-Studio accounts intentionally have no active_relationship.
        // Use a hint only when this account actually has a Studio in that Brand;
        // without a hint, a single Brand is unambiguous. Never pick an arbitrary
        // Brand when several are linked to the same account.
        const brand = entry.brandHint
          ? (brands.includes(entry.brandHint) ? entry.brandHint : null)
          : context.active_relationship?.studio?.brand_network?.slug || (brands.length === 1 ? brands[0] : null);
        studio = studios.find(item => brand && item.brand_network?.slug === brand);
      }
      if (!active) return;
      // Neither the old URL's Brand hint nor another account's local cache
      // grants access. Resolve the Brand and preference through current APIs.
      const brand = studio?.brand_network?.slug;
      if (brand) {
        const data = await api.get(`/client/brands/${encodeURIComponent(brand)}/home`);
        if (!active) return;
        const home = data.home || (data.studios?.length === 1 ? data.studios[0] : null);
        cacheBrandHome(brand, home);
        const destination = home && brandHomeUrl(brand, home.slug);
        if (destination) {
          setTarget(destination);
          setState('redirecting');
          navigate(destination, {replace: true});
          return;
        }
      }
      setState('done');
    };
    run().catch(error => {
      if (active) setState(error?.response?.status === 404 ? 'done' : 'error');
    });
    return () => {active = false;};
  }, [entry, status, user?.id, user?.role, navigate, retry, state, location.search]);

  useEffect(() => {
    if (state === 'redirecting' && location.pathname + location.search === target) setState('done');
  }, [state, target, location.pathname, location.search]);

  if (!entry || state === 'done' || (status !== 'loading' && (status !== 'authed' || user?.role !== 'client'))) return children;
  if (state === 'error') return <main className="grid min-h-screen place-items-center bg-zinc-950 p-6 text-white"><section role="alert" className="max-w-md space-y-4"><h1 className="text-xl">{tr("Non riesco ad aprire la tua sede")}</h1><p>{tr("Controlla la connessione e riprova.")}</p><button className="min-h-11 rounded border px-4" onClick={() => {setState('loading'); setRetry(value => value + 1);}}>{tr("Riprova")}</button></section></main>;
  return <main className="grid min-h-screen place-items-center bg-zinc-950 p-6 text-white"><p role="status">{tr("Apro la tua sede…")}</p></main>;
}
